import React, { useMemo, useState } from "react";
import { css } from "@linaria/core";
import { AddCircleLine, DeleteBinLine, ListCheck, PencilLine } from "@ui/IconV2/react";
import classNames from "classnames";
import t from "../../../../../../../../i18n";
import { colors, spacings } from "@ui/Theme";
import Text from "@ui/Text";
import { TableCardWithoutBorder } from "@ui/Table/_styles/TableCardWithoutBorder";
import TableCard from "@ui/Table/Card";
import Table from "@ui/Table";
import createIOSortType from "../../../../../../../../lib/createIOSortType";
import createGlobalState from "@ui/lib/createGlobalState";
import type { ManualFaultEvent } from "../../../../../../../../api/useManualFaultEvents";
import useSortedData from "../../../../../../../../lib/hooks/useSortedData";
import type { SortableValue } from "diagnostics-lib/comparator";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import Button from "@ui/Button";
import formatDate from "@ui/i18n/formatDate";
import type { Project } from "../../../../../../../../api/useProjects";
import { canUserDoOnProject } from "diagnostics-domain/canUserDo";
import { useUser } from "../../../../../../../../api/useUser";
import TooltipV2 from "@ui/TooltipV2";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { useModals } from "@ui/lib/hooks/useModal";
import type { Quantity } from "../../../../../../../../api/useQuantities";
import { indexBy } from "diagnostics-lib/iterable";
import Ellipsis from "@ui/Ellipsis";
import Modal from "@ui/Modal";
import ModifyModalForm from "./ModalForm/ModifyModalForm";
import AddPeriodModalForm from "./ModalForm/AddPeriodModalForm";
import DeleteModalForm from "./ModalForm/DeleteModalForm";
import usePerformanceIndicators from "../../../../../../../../api/usePerformanceIndicators";

type TimeWindowInput = {
  id: string;
  startDate: number;
  endDate: number;
  impacts: {
    quantityId: string;
    quantityName: string;
    unit: string | null;
    value: number;
  }[];
};
const columns = ["startDate", "endDate"] as const;

const useSortInput = createGlobalState({
  storage: "session",
  key: "crossFilterSortEvent",
  type: createIOSortType(columns),
  defaultValue: {
    column: "startDate",
    direction: "asc",
  },
  scoped: true,
});
type Column = (typeof columns)[number];

const sortSelector: { [key in Column]: (b: TimeWindowInput) => SortableValue | null } = {
  startDate: b => b.startDate,
  endDate: b => b.endDate,
};

const computeRowKey = (timeWindow: TimeWindowInput) =>
  `${timeWindow.startDate}-${timeWindow.endDate}`;

interface ManualFaultImpactTimeWindowsChangeProps {
  project: Project;
  quantities: Quantity[];
  manualFaultEvent: ManualFaultEvent;
  className?: string;
}

const ManualFaultImpactTimeWindowsChange = ({
  project,
  manualFaultEvent,
  quantities,
  className,
}: ManualFaultImpactTimeWindowsChangeProps) => {
  const user = useUser();
  const canWrite = canUserDoOnProject(user, "PROJECT_WRITE", project.id);

  const { modal, openModal, closeModal } = useModals<"add" | "edit" | "delete">();
  const [sort, setSort] = useSortInput({ scope: manualFaultEvent.id });

  const indexedQuantitiesById = useMemo(() => indexBy(quantities, q => q.id), [quantities]);
  const performanceIndicators = usePerformanceIndicators(project.id);

  const inputDatas = useMemo(
    () =>
      manualFaultEvent.windows.map(m => {
        const impacts = manualFaultEvent.impacts.filter(
          i => i.manualFaultImpactTimeWindowId === m.id,
        );

        return {
          id: m.id,
          startDate: new Date(m.startsAt).getTime(),
          endDate: new Date(m.endsAt).getTime(),
          impacts,
        };
      }),
    [manualFaultEvent.windows, manualFaultEvent.impacts],
  );
  const [selectedTimeWindowId, setSelectedTimeWindowId] = useState<string | null>(null);

  const sortedCrossFilters = useSortedData(inputDatas, sortSelector[sort.column], sort.direction);

  const inputMetadataDynamic: {
    [key in string]: ColumnMetaData<TimeWindowInput>;
  } = useMemo(
    () => ({
      ...performanceIndicators.reduce(
        (acc, pi) => {
          acc[pi.name] = {
            label: (
              <Ellipsis>
                {t("project.event.manual_fault.tables.impacts_time_windows_change.label", {
                  quantityName: pi.name,
                })}
              </Ellipsis>
            ),
            render: (b: TimeWindowInput) => {
              const impact = b.impacts.find(i => i.quantityId === pi.id);
              if (impact) {
                const unit = indexedQuantitiesById.get(pi.id)?.unit ?? "";
                return `${impact.value.toFixed(1)}${unit ? ` ${unit}` : ""}`;
              }
              return null;
            },
            minWidth: "150px",
            sortable: false,
          };
          return acc;
        },
        {} as { [key in string]: ColumnMetaData<TimeWindowInput> },
      ),
    }),
    [performanceIndicators, indexedQuantitiesById],
  );

  const inputMetadata: { [key in Column | "action"]: ColumnMetaData<TimeWindowInput> } & {
    [key: string]: ColumnMetaData<TimeWindowInput>;
  } = useMemo(
    () => ({
      startDate: {
        label: t(
          "project.event.manual_fault.diagnosis.evolution.impact_time_window.metatadata.start_date",
        ),
        render: (b: TimeWindowInput) => formatDate(b.startDate, "slash", project.timezone),
        minWidth: "200px",
        sortable: true,
      },
      endDate: {
        label: t(
          "project.event.manual_fault.diagnosis.evolution.impact_time_window.metatadata.end_date",
        ),
        render: (b: TimeWindowInput) => formatDate(b.endDate, "slash", project.timezone),
        minWidth: "200px",
        sortable: true,
      },
      ...inputMetadataDynamic,
      action: {
        label: "Actions",
        hidden: !canWrite,
        render: (b: TimeWindowInput) => {
          const disableDelete = inputDatas.length < 2;
          return (
            <div className={rowActions}>
              <TooltipV2
                overlay={t(
                  "project.event.manual_fault.diagnosis.evolution.impact_time_window.metadata.actions.edit",
                )}
              >
                <ButtonIconV2
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedTimeWindowId(b.id || null);
                    openModal("edit");
                  }}
                >
                  <PencilLine />
                </ButtonIconV2>
              </TooltipV2>
              <TooltipV2
                overlay={
                  disableDelete
                    ? undefined
                    : t(
                        "project.event.manual_fault.diagnosis.evolution.impact_time_window.metadata.actions.delete",
                      )
                }
              >
                <ButtonIconV2
                  title={
                    disableDelete
                      ? t(
                          "project.event.manual_fault.diagnosis.evolution.impact_time_window.metadata.actions.impossible_to_delete",
                        )
                      : undefined
                  }
                  disabled={disableDelete}
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedTimeWindowId(b.id || null);
                    openModal("delete");
                  }}
                >
                  <DeleteBinLine />
                </ButtonIconV2>
              </TooltipV2>
            </div>
          );
        },
        minWidth: "82px",
        maxWidth: "82px",
        sticky: true,
        stickyDirection: "right",
        stickyBorder: true,
      },
    }),
    [canWrite, inputMetadataDynamic, openModal, project.timezone, inputDatas],
  );

  const selectTimeWindow = useMemo(() => {
    const timeWindow = inputDatas.find(tw => tw.id === selectedTimeWindowId);
    if (!timeWindow) return null;

    return timeWindow;
  }, [selectedTimeWindowId, inputDatas]);

  return (
    <>
      <div className={classNames(className, manualFaultImpactsTWChange)}>
        <div className={title}>
          <ListCheck color={colors.carbon800} />
          <Text>
            {t(
              "project.event.manual_fault.tab.diagnosis.evolution_chart.impact_time_windows_change.title",
            )}
          </Text>
          <Button
            leftIcon={<AddCircleLine />}
            variant="secondary"
            slim
            onClick={() => openModal("add")}
          >
            {t(
              "project.event.manual_fault.tab.diagnosis.evolution_chart.impact_time_windows_change.title.button.add_time_window",
            )}
          </Button>
        </div>

        <div className={content}>
          <Text type="body2" className={description}>
            {t(
              "project.event.manual_fault.tab.diagnosis.evolution_chart.impact_time_windows_change.description",
            )}
          </Text>

          <TableCard
            className={classNames(TableCardWithoutBorder, tableMargin)}
            title={null}
            headerLeft={null}
            headerRight={null}
          >
            <Table
              stickyHeader
              datas={sortedCrossFilters}
              metadata={inputMetadata}
              sort={sort}
              onSort={setSort}
              computeRowKey={computeRowKey}
            />
          </TableCard>
        </div>
      </div>
      <Modal
        title={t(
          "project.event.manual_fault.diagnosis.evolution.impact_time_window.modal.add.title",
        )}
        isOpen={modal === "add"}
        onRequestClose={closeModal}
        closable
      >
        <AddPeriodModalForm
          project={project}
          quantities={quantities}
          manualFaultEvent={manualFaultEvent}
          closeModal={closeModal}
        />
      </Modal>
      {selectTimeWindow && (
        <>
          <Modal
            title={t(
              "project.event.manual_fault.diagnosis.evolution.impact_time_window.modal.edit.title",
            )}
            isOpen={modal === "edit"}
            onRequestClose={closeModal}
            closable
          >
            <ModifyModalForm
              period={selectTimeWindow.id !== inputDatas.at(0)?.id}
              project={project}
              manualFaultEvent={manualFaultEvent}
              timeWindow={selectTimeWindow}
              closeModal={closeModal}
            />
          </Modal>
          <Modal
            size="l"
            title={t(
              "project.event.manual_fault.diagnosis.evolution.impact_time_window.modal.delete.title",
            )}
            isOpen={modal === "delete"}
            onRequestClose={closeModal}
            closable
          >
            <DeleteModalForm
              project={project}
              manualFaultEventId={manualFaultEvent.id}
              manualFaultEventStartsAt={manualFaultEvent.startsAt.getTime()}
              timeWindow={selectTimeWindow}
              closeModal={closeModal}
            />
          </Modal>
        </>
      )}
    </>
  );
};

export default ManualFaultImpactTimeWindowsChange;

// #region CSS
const manualFaultImpactsTWChange = css`
  overflow: hidden;
`;
const title = css`
  display: inline-flex;
  align-items: center;
  gap: ${spacings.xxs};
  width: 100%;

  button {
    margin-left: auto;
  }
`;
const content = css`
  margin-top: ${spacings.xxs};
  margin-left: 20px;
`;
const tableMargin = css`
  margin-top: ${spacings.xs};

  thead {
    top: 0 !important;
  }
`;
const description = css`
  color: ${colors.carbon800};
`;
const rowActions = css`
  display: flex;
`;
// #endregion CSS
